import { createFileRoute, notFound } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { PageBodyContainer } from '@repo/ui/components/layouts/page-body-container.tsx';
import { TopBarWithLogo } from '@repo/ui/components/layouts/top-bar-with-logo.tsx';

import { AppFooter } from '~/components/footer.tsx';
import { PageLoader } from '~/components/loader.tsx';
import { NotFound } from '~/components/not-found.tsx';
import { MedicalHistoryForm } from '~/domains/medical-history/components/medical-history-form.tsx';
import { normalizeUrl } from '~/utils/url.ts';

const schema = z.object({
  rest: fallback(z.string(), ''),
});

export const Route = createFileRoute('/$pathLocale/medical-history')({
  component: RouteComponent,
  validateSearch: zodValidator(schema),
  notFoundComponent: NotFound,
  pendingComponent: () => (
    <div className="h-screen flex items-center">
      <PageLoader className="size-16" />
    </div>
  ),
  loaderDeps: (ctx) => ({
    rest: ctx.search.rest,
  }),
  loader: async (ctx) => {
    if (!ctx.deps.rest) {
      throw notFound();
    }

    const url = new URL(ctx.deps.rest, normalizeUrl(ctx.context.appConfig.restEndpoint)).toString();

    return await fetch(url, {
      cache: 'no-cache',
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error();
        }
        return {
          medicalHistory: await res.json(),
          restUrl: url,
        };
      })
      .catch(() => {
        throw notFound();
      });
  },
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const { t } = useTranslation(['common', 'members']);

  return (
    <>
      <TopBarWithLogo>
        <h4 className="sm:text-lg text-center md:text-left uppercase">
          {t('members:medical-history.member-page-title')}
        </h4>
      </TopBarWithLogo>
      <PageBodyContainer className="h-screen">
        <div className="p-4">
          <MedicalHistoryForm medicalHistory={data.medicalHistory} restUrl={data.restUrl} />
        </div>
      </PageBodyContainer>
      <AppFooter />
    </>
  );
}
